import { Component } from '@theme/component';
import { onAnimationEnd, prefersReducedMotion, debounce, preloadImage } from '@theme/utilities';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

/**
 * A custom element that renders a zoom dialog.
 *
 * One media fills the dialog at a time and the arrows move between them (Figma nodes
 * 6350:74803 / 6350:74824 desktop, 6350:100129 / 6350:100135 mobile). The two zoom levels
 * the design draws — the media fitted to the dialog, then magnified and pannable — belong
 * to `drag-zoom-wrapper`; this component only puts it back to the fitted one when the
 * media under it changes.
 *
 * The close button and Escape are the only ways out. A click on the media is the zoom, and
 * nothing else in the dialog closes it - a click that lands beside the media at one zoom
 * level lands on it at the other, so closing on one of them would close the dialog out from
 * under a shopper who was only zooming back out.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} gallery - The scroller holding the media.
 * @property {HTMLElement[]} media - The media elements.
 * @property {HTMLElement} [bar] - The product bar along the bottom edge.
 * @property {HTMLButtonElement} [previousButton] - The previous-media arrow.
 * @property {HTMLButtonElement} [nextButton] - The next-media arrow.
 * @property {HTMLButtonElement} [buyButton] - The bar's CTA.
 * @property {HTMLElement} [buyButtonText] - The CTA's label.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'gallery'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());

  /** @type {number} */
  #activeIndex = 0;

  connectedCallback() {
    super.connectedCallback();
    this.refs.gallery.addEventListener('scroll', this.handleScroll);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.gallery.removeEventListener('scroll', this.handleScroll);
  }

  /**
   * Opens the zoom dialog. The dialog fades itself in from CSS, so there is nothing to
   * orchestrate here beyond putting it on the right media.
   *
   * Only showing the dialog on the right media happens on the tap itself. The rest of the
   * setup waits for the dialog's first frame to paint, since INP counts until then: that
   * frame is fully transparent (the fade starts from `opacity: 0`), so nothing the setup
   * changes can show in it. Without the fade (reduced motion) the dialog is opaque from its
   * first frame, and the setup stays on the tap so that frame is already right.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  open(index, event) {
    event.preventDefault();

    this.refs.dialog.showModal();
    this.#scrollToIndex(index, 'instant');

    const setUp = () => {
      this.#setActiveIndex(index, { resetZoom: true });
      this.syncBuyButton();
    };

    if (prefersReducedMotion()) setUp();
    else requestAnimationFrame(() => setTimeout(setUp, 0));
  }

  /**
   * Loads a high-resolution image for a specific media container
   * @param {HTMLElement} mediaContainer - The media container element
   */
  loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;

    preloadImage(highResolutionUrl);

    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.setAttribute('data_max_resolution', highResolutionUrl);
    newImage.setAttribute('ref', 'image');

    // When the high-resolution image loads, replace the existing image
    newImage.onload = () => {
      image.replaceWith(newImage);
      this.#highResImagesLoaded.add(highResolutionUrl);
    };

    newImage.src = highResolutionUrl;
  }

  /**
   * Handles the scroll event of the gallery, which keeps the active media in step when the
   * scroller settles somewhere the arrows did not put it.
   */
  handleScroll = debounce(async () => {
    const { media } = this.refs;

    const mostVisibleElement = await getMostVisibleElement(media);
    const activeIndex = media.indexOf(mostVisibleElement);

    if (activeIndex === -1) return;

    this.#setActiveIndex(activeIndex);
  }, 50);

  /**
   * Moves to the previous media.
   */
  previous() {
    this.#step(-1);
  }

  /**
   * Moves to the next media.
   */
  next() {
    this.#step(1);
  }

  /**
   * Moves the gallery by one media, wrapping around at either end so neither arrow is ever
   * a dead control — the design draws them both live.
   *
   * @param {number} delta - `-1` for the previous media, `1` for the next one.
   */
  #step(delta) {
    const { media } = this.refs;
    const count = media.length;

    if (count < 2) return;

    const index = (this.#activeIndex + delta + count) % count;

    // A wrap crosses the whole gallery. Flying past every media in between reads as a
    // glitch rather than as movement, so only a step to a neighbour animates.
    const wrapped = Math.abs(index - this.#activeIndex) > 1;

    this.#setActiveIndex(index);
    this.#scrollToIndex(index, prefersReducedMotion() || wrapped ? 'instant' : 'smooth');
  }

  /**
   * Brings a media into view.
   *
   * @param {number} index - The index of the media to scroll to.
   * @param {ScrollBehavior} behavior - The behavior of the scroll.
   */
  #scrollToIndex(index, behavior) {
    this.refs.media[index]?.scrollIntoView({ behavior, block: 'nearest', inline: 'center' });
  }

  /**
   * Records which media is being looked at, and tells the gallery behind the dialog so the
   * two are on the same image when it closes.
   *
   * @param {number} index - The index of the active media.
   * @param {object} [options] - The options for the change.
   * @param {boolean} [options.resetZoom] - Put the media back to the fitted zoom level even
   *   when the index has not moved (on open, after a previous visit left one magnified).
   */
  #setActiveIndex(index, options = {}) {
    const { media } = this.refs;
    const target = media[index];

    if (!target) return;

    const changed = index !== this.#activeIndex;
    this.#activeIndex = index;

    // A magnified media the shopper navigates away from should not still be magnified when
    // they come back to it, and the media arriving is drawn fitted.
    if (changed || options.resetZoom) this.#resetZoom();

    this.loadHighResolutionImage(target);
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }

  /**
   * Puts every media back to the fitted zoom level.
   */
  #resetZoom() {
    for (const wrapper of this.querySelectorAll('drag-zoom-wrapper')) {
      if ('reset' in wrapper && typeof wrapper.reset === 'function') wrapper.reset();
    }
  }

  /**
   * The PDP's own add-to-cart button, which the bar's CTA stands in for while the dialog
   * covers it. Scoped to the product this gallery belongs to, so the quick-add drawer —
   * which renders this same gallery — reaches its own rather than the page's.
   *
   * @returns {HTMLButtonElement | HTMLAnchorElement | null}
   */
  get #pageBuyButton() {
    const root = this.closest('product-component') ?? this.closest('.shopify-section');

    return root?.querySelector('.buy-buttons-block .add-to-cart-button') ?? null;
  }

  /**
   * The picker the bar's CTA points at, found from the same root as the CTA itself so the
   * two can only ever come from one product.
   *
   * @returns {Element | null}
   */
  get #variantPicker() {
    const root = this.closest('product-component') ?? this.closest('.shopify-section');

    return root?.querySelector('variant-picker') ?? null;
  }

  /**
   * Takes the CTA's label from the PDP's own, so a size the shopper picked before opening
   * the zoom is reflected in what the bar offers. The server-rendered label covers the
   * first paint and the no-JS case.
   */
  syncBuyButton() {
    const { buyButton, buyButtonText } = this.refs;

    if (!buyButton || !buyButtonText) return;

    const pageBuyButton = this.#pageBuyButton;

    // Nothing to stand in for — a gallery without buy buttons beside it (the theme editor's
    // visual preview, a section rendered on its own) gets no CTA rather than a dead one.
    buyButton.hidden = !pageBuyButton;

    const label = pageBuyButton?.querySelector('.add-to-cart-text__content')?.textContent?.trim();
    if (label) buyButtonText.textContent = label;

    // The state as well as the wording, with one exception: a CTA only waiting on a size
    // stays live here. Its click is the way back to the picker rather than an add (see
    // `handleBuyClick`), and the dialog is covering the page that picker is on — leaving
    // it dead would leave the shopper nothing to act on but the close button. Sampling
    // here is enough either way: the dialog is modal, so nothing can move the page's CTA
    // while it is open.
    buyButton.disabled = this.#pageBuyDisabled && !this.#awaitingSize;
  }

  /**
   * Whether the page behind the dialog has yet to be given a size.
   *
   * Read off the page's own CTA, which carries the answer as `data-awaiting-size` and is
   * kept current on the size click itself by `previewSizeSelection` in product-form.js,
   * ahead of the render. This used to be inferred from that CTA still being dead, which
   * is no longer a signal: the awaiting-size CTA is live at every width, because on
   * mobile it is the one pinned to the bottom of the screen and a dead button there
   * leaves the shopper nothing to act on. `data-awaiting-size` on this element is the
   * fallback - the page's answer at render time - for a gallery with no CTA to read.
   *
   * @returns {boolean}
   */
  get #awaitingSize() {
    const container = this.#pageBuyButton?.closest('add-to-cart-component');

    if (container instanceof HTMLElement) return container.dataset.awaitingSize === 'true';

    return this.dataset.awaitingSize === 'true';
  }

  /**
   * Whether the page's CTA is one a click can do anything with. `aria-disabled` as well
   * as `disabled`: the CTA renders as a link when it carries an href, and a link cannot
   * be disabled natively.
   */
  get #pageBuyDisabled() {
    const button = this.#pageBuyButton;

    if (!button) return true;

    return ('disabled' in button && button.disabled) || button.getAttribute('aria-disabled') === 'true';
  }

  /**
   * Hands the shopper back to the PDP: the dialog closes, and then either the add happens
   * or - for the CTA that is still asking for a size - the Size field comes on screen. The
   * add itself stays with the real form; a second one inside the dialog would be a second
   * source of truth for the same variant.
   *
   * `#showSizePicker` also catches a click that arrives on a CTA the page will not act on
   * for some other reason, which `syncBuyButton` keeps disabled - so that case is a guard
   * against the two ever drifting rather than a path the bar offers.
   */
  async handleBuyClick() {
    const pageBuyButton = this.#pageBuyButton;
    const awaitingSize = this.#awaitingSize;
    const disabled = this.#pageBuyDisabled;

    await this.close();

    if (!pageBuyButton) return;

    // Taken here rather than left to the page CTA's own click. That button would do the
    // same jump (`handleClick` in assets/product-form.js), but the dialog was covering the
    // picker and may have been opened with it already on screen - `showSizePicker` is the
    // one that knows not to scroll in that case.
    if (awaitingSize) {
      this.#showSizePicker(pageBuyButton);
      return;
    }

    if (!disabled) {
      pageBuyButton.click();
      return;
    }

    this.#showSizePicker(pageBuyButton);
  }

  /**
   * Brings the Size field back on screen, with the keyboard on the first size that can be
   * picked.
   *
   * The Size fieldset rather than the picker as a whole - Colour sits above it and the CTA
   * is asking for a size - falling back to the picker, and then to the CTA itself, for a
   * product whose Size option isn't drawn as a grid.
   *
   * Only when it isn't already there, so a shopper who zoomed an image with the picker
   * still on screen gets their page back exactly as they left it. `scrollIntoView` and no
   * offset arithmetic of its own: on desktop the picker rides the pinned details column,
   * where a computed delta is undone by the column re-pinning (the case
   * product-more-details.js does that arithmetic for), and `block: 'center'` clears the
   * header above and the mobile buy bar below at every width either way.
   *
   * @param {HTMLElement} fallback - Where to go when there is no picker to point at.
   */
  #showSizePicker(fallback) {
    const picker = this.#variantPicker;
    const target = picker?.querySelector('.variant-option--sizes') ?? picker ?? fallback;

    if (!(target instanceof HTMLElement)) return;

    if (!isFullyInView(target)) {
      target.scrollIntoView({
        behavior: prefersReducedMotion() ? 'instant' : 'smooth',
        block: 'center',
      });
    }

    // Focus follows the jump, so a keyboard or screen reader user carries on from the
    // field the CTA named rather than from a dialog that is no longer there. `disabled` is
    // worn only by sizes the selected fit doesn't run - a sold-out one is still a size to
    // pick, since picking it turns the CTA into the back-in-stock notice. The scroll above
    // has already placed the field, hence `preventScroll`.
    const firstSize = target.querySelector('input:not([disabled])');

    if (firstSize instanceof HTMLElement) firstSize.focus({ preventScroll: true });
  }

  /**
   * Closes the zoom dialog on a fade. A closed `<dialog>` is `display: none`, so it cannot
   * fade out on its own once `close()` is called - `dialog--closed` carries the fade and is
   * held on until it finishes, and only then is the element closed. Reduced motion goes
   * straight there.
   */
  async close() {
    const { dialog } = this.refs;

    if (prefersReducedMotion()) return this.closeDialog();

    dialog.classList.add('dialog--closed');
    await onAnimationEnd(dialog);
    this.closeDialog();
    dialog.classList.remove('dialog--closed');
  }

  closeDialog() {
    const { dialog } = this.refs;
    dialog.close();
    window.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * Closes the dialog on escape, and moves between media on the arrow keys — the arrows are
   * the only way through the gallery now that the thumbnail rail is gone.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        this.close();
        break;

      case 'ArrowLeft':
        event.preventDefault();
        this.previous();
        break;

      case 'ArrowRight':
        event.preventDefault();
        this.next();
        break;
    }
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}

/**
 * Whether `element` is entirely on screen, clear of the chrome the theme pins over the
 * page: the sticky header above it, and below it the PDP buy bar that
 * sections/product-information.liquid pins to the bottom edge on mobile
 * (`--mobile-buy-bar-height`, measured by assets/mobile-buy-bar.js; 0 where there is no
 * bar).
 *
 * @param {HTMLElement} element - The element to test.
 * @returns {boolean}
 */
function isFullyInView(element) {
  // Only an `always` sticky header is over the content once the page has been scrolled -
  // the same reading of `--header-height` that hero-slider.js and product-more-details.js
  // take.
  const header = document.querySelector('header-component[sticky="always"]')
    ? parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height'))
    : 0;
  const buyBar = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--mobile-buy-bar-height'));

  const top = Number.isFinite(header) ? header : 0;
  const bottom = window.innerHeight - (Number.isFinite(buyBar) ? buyBar : 0);

  const rect = element.getBoundingClientRect();

  return rect.top >= top && rect.bottom <= bottom;
}

/**
 * Get the most visible element from a list of elements.
 * @param {HTMLElement[]} elements - The elements to get the most visible element from.
 * @returns {Promise<HTMLElement>} A promise that resolves to the most visible element.
 */
function getMostVisibleElement(elements) {
  return new Promise((resolve) => {
    const observer = new IntersectionObserver(
      (entries) => {
        const mostVisible = entries.reduce((prev, current) =>
          current.intersectionRatio > prev.intersectionRatio ? current : prev
        );
        observer.disconnect();
        resolve(/** @type {HTMLElement} */ (mostVisible.target));
      },
      {
        threshold: Array.from({ length: 100 }, (_, i) => i / 100),
      }
    );

    for (const element of elements) {
      observer.observe(element);
    }
  });
}
